/**
 * CreateExperimentStep2
 *
 * Figma implementation of the Step 2 "Processing Options" body.
 * Design source: Figma (Step 2 expanded)
 *
 *
 */

import React from 'react';

import { Checkbox, NumberInput, Tooltip } from '@mantine/core';

import dropdownIcon from '../assets/drop-down-chevron.svg';
import helpIcon from '../assets/icon-help-circle.svg';
import { ClonotypeDefinition, getClonotypeDefinitionTooltip } from '../store/slices/preprocessing.slice';

/* ─── Props ────────────────────────────────────────────────────────── */

export interface CreateExperimentStep2Props {
  useDefaultClonotyping: boolean;
  onUseDefaultClonotypingChange: (value: boolean) => void;
  includeUnpairedHeavyChains: boolean;
  onIncludeUnpairedHeavyChainsChange: (value: boolean) => void;
  clonotypeDefinition: ClonotypeDefinition;
  onClonotypeDefinitionChange: (value: ClonotypeDefinition) => void;
  threshold: number;
  onThresholdChange: (value: number) => void;
}

/* ─── Clonotype Definition Options ─────────────────────────────────── */

const customClonotypeOptions: { value: ClonotypeDefinition; label: string }[] = [
  { value: ClonotypeDefinition.CDR3_IDENTITY, label: getClonotypeDefinitionTooltip(ClonotypeDefinition.CDR3_IDENTITY) }, // default when not using HILARy
  { value: ClonotypeDefinition.CDR3_LENGTH, label: getClonotypeDefinitionTooltip(ClonotypeDefinition.CDR3_LENGTH) },
  { value: ClonotypeDefinition.CDR3_ONLY, label: getClonotypeDefinitionTooltip(ClonotypeDefinition.CDR3_ONLY) },
];

/* ─── Component ────────────────────────────────────────────────────── */

export function CreateExperimentStep2({
  useDefaultClonotyping,
  onUseDefaultClonotypingChange,
  includeUnpairedHeavyChains,
  onIncludeUnpairedHeavyChainsChange,
  clonotypeDefinition,
  onClonotypeDefinitionChange,
  threshold,
  onThresholdChange,
}: CreateExperimentStep2Props) {
  const handleDefaultClonotypingChange = (checked: boolean) => {
    onUseDefaultClonotypingChange(checked);
    if (checked) {
      onClonotypeDefinitionChange(ClonotypeDefinition.HILARY);
    } else if (clonotypeDefinition === ClonotypeDefinition.HILARY) {
      onClonotypeDefinitionChange(ClonotypeDefinition.CDR3_IDENTITY); // set to a non-HILARY default when unchecked and currently set to HILARY
    }
  };

  const isCustomDisabled = useDefaultClonotyping;

  return (
    <div className="flex flex-col items-start gap-2 px-[90px] w-full flex-1 overflow-auto">
      {/* ── Clonotyping Section ─────────────────────────────────── */}
      <div className="flex flex-col w-full">
        <div className="flex items-center gap-1">
          <span className="text-form-label">Clonotyping</span>
          <Tooltip
            label="Identifies clonal lineages using V/J assignment and CDR3 identity"
            position="right"
            withArrow
            arrowSize={10}
            withinPortal
            portalProps={{ style: { position: 'absolute', zIndex: 10001 } }}
            classNames={{ tooltip: 'help-tooltip', arrow: 'help-tooltip-arrow' }}
          >
            <img src={helpIcon} alt="Help" className="w-4 h-4 cursor-pointer" onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </div>

        <div className="form-accordion-content flex flex-col gap-4">
          {/* HILARy default checkbox */}
          <Checkbox
            color="#0064B0"
            checked={useDefaultClonotyping}
            onChange={(e) => handleDefaultClonotypingChange(e.currentTarget.checked)}
            label={
              <span>
                HILARy Algorithm: recommended statistical model.{' '}
                {/* <a href="https://elifesciences.org/articles/86181" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Learn more...
                </a> */}
              </span>
            }
            classNames={{ label: "font-['Rubik'] text-[13px] text-neutral-800" }}
          />

          {/* Custom controls — disabled when default is checked */}
          <div className={isCustomDisabled ? 'form-controls-disabled' : ''}>
            {/* Custom Definition */}
            <div className="flex flex-col gap-1">
              <div className="flex flex-row gap-[3px] items-start">
                <span className={`text-form-label ${isCustomDisabled ? 'text-form-border' : 'text-neutral-800'}`}>Custom Definition</span>
              </div>

              <div className="relative">
                <select
                  className="form-select"
                  value={clonotypeDefinition}
                  onChange={(e) => onClonotypeDefinitionChange(e.target.value as ClonotypeDefinition)}
                  disabled={isCustomDisabled}
                >
                  {customClonotypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-[10px] top-1/2 -translate-y-1/2 pointer-events-none">
                  <img src={dropdownIcon} alt="" className="w-[10px] h-[6px]" />
                </div>
              </div>
            </div>

            {/* Threshold: only enabled for CDR3 Identity and CDR3 Only */}
            <div className="mt-4">
              <span className="text-form-label">CDR3 Identity Threshold</span>
              <NumberInput
                value={threshold * 100}
                onChange={(percentage) => onThresholdChange(typeof percentage === 'number' ? percentage / 100 : 0.85)}
                min={0}
                max={100}
                step={1}
                decimalScale={0}
                suffix="%"
                disabled={
                  isCustomDisabled || (clonotypeDefinition !== ClonotypeDefinition.CDR3_IDENTITY && clonotypeDefinition !== ClonotypeDefinition.CDR3_ONLY)
                }
                w={80}
                classNames={{ label: `font-['Rubik'] text-[13px] ${isCustomDisabled ? 'text-form-border' : 'text-neutral-800'}` }}
              />
            </div>
          </div>

          {/* Include unpaired heavy chains checkbox */}
          <Checkbox
            color="#0064B0"
            checked={includeUnpairedHeavyChains}
            onChange={(e) => onIncludeUnpairedHeavyChainsChange(e.currentTarget.checked)}
            label="Include unpaired heavy chains in clonotyping (recommended)"
            classNames={{ label: "font-['Rubik'] text-[13px] text-neutral-800" }}
          />
        </div>
      </div>
    </div>
  );
}

export default CreateExperimentStep2;
